import Phaser from "phaser";
import { NATIVE_WIDTH, PALETTE, UGC_CYAN } from "../config";
import { ROW_HEIGHT, STACK_TOP_Y } from "../scenes/board/geometry";
import { t } from "../i18n";

const DEPTH_HUD = 20;

/** Right-edge scroll widget: up/down arrows, track, proportional cursor,
 *  "{from}–{to} / {total}" label, and an optional out-of-view "NEW ▲" flash.
 *  Extracted from ShopScene so both market rooms share it. */
export class ScrollIndicator {
  private readonly track: Phaser.GameObjects.Rectangle;
  private readonly upArrow: Phaser.GameObjects.Triangle;
  private readonly downArrow: Phaser.GameObjects.Triangle;
  private readonly cursor: Phaser.GameObjects.Rectangle;
  private readonly label: Phaser.GameObjects.Text;
  private readonly newHint: Phaser.GameObjects.Text;

  constructor(private readonly scene: Phaser.Scene, visibleRows: number) {
    const toHex = (c: number) => `#${c.toString(16).padStart(6, "0")}`;
    const x = NATIVE_WIDTH - 16;
    const trackTop = STACK_TOP_Y;
    const trackHeight = visibleRows * ROW_HEIGHT;
    const trackBottom = trackTop + trackHeight;

    this.track = scene.add
      .rectangle(x, trackTop, 2, trackHeight, PALETTE.shopWallHi, 0.7)
      .setOrigin(0.5, 0)
      .setDepth(DEPTH_HUD);
    this.track.setStrokeStyle(1, PALETTE.shopFloorEdge);
    this.upArrow = scene.add
      .triangle(x, trackTop - 6, 0, -3, -4, 3, 4, 3, PALETTE.parchment)
      .setDepth(DEPTH_HUD);
    this.downArrow = scene.add
      .triangle(x, trackBottom + 6, 0, 3, -4, -3, 4, -3, PALETTE.parchment)
      .setDepth(DEPTH_HUD);
    this.cursor = scene.add
      .rectangle(x, trackTop, 6, 12, UGC_CYAN, 1)
      .setOrigin(0.5, 0)
      .setDepth(DEPTH_HUD + 1);
    this.cursor.setStrokeStyle(1, PALETTE.parchmentInk);
    this.label = scene.add
      .text(x, trackBottom + 14, "", {
        fontFamily: "monospace",
        fontSize: "6px",
        color: toHex(PALETTE.parchment),
      })
      .setOrigin(0.5, 0)
      .setDepth(DEPTH_HUD);
    this.newHint = scene.add
      .text(x - 8, trackTop - 6, t("shop.scroll.new"), {
        fontFamily: "monospace",
        fontSize: "7px",
        color: toHex(UGC_CYAN),
        fontStyle: "bold",
      })
      .setOrigin(1, 0.5)
      .setDepth(DEPTH_HUD)
      .setAlpha(0);
  }

  update(offset: number, total: number, visibleRows: number): void {
    const visible = Math.min(visibleRows, total);
    const maxOffset = Math.max(0, total - visibleRows);
    const trackTop = this.track.y;
    const trackHeight = this.track.height;
    const cursorHeight =
      total === 0 ? trackHeight : Math.max(8, Math.floor((visible / total) * trackHeight));
    const ratio = maxOffset === 0 ? 0 : offset / maxOffset;
    this.cursor.setSize(this.cursor.width, cursorHeight);
    this.cursor.y = trackTop + ratio * (trackHeight - cursorHeight);
    this.upArrow.setAlpha(offset > 0 ? 1 : 0.35);
    this.downArrow.setAlpha(offset < maxOffset ? 1 : 0.35);
    if (total === 0) {
      this.label.setText("0 / 0");
    } else {
      this.label.setText(`${offset + 1}–${Math.min(total, offset + visibleRows)} / ${total}`);
    }
  }

  /** Pulse the "NEW ▲" hint 3× then fade (shop-only, out-of-view arrival). */
  flashNewHint(): void {
    this.newHint.setAlpha(0);
    this.scene.tweens.add({ targets: this.newHint, alpha: 1, duration: 160, yoyo: true, repeat: 2 });
  }
}
